import React, { useState, useEffect } from 'react';
import {
  BarChart2, TrendingUp, FileText, Calendar,
  Download, ChevronRight, CheckCircle, Clock,
  XCircle, AlertCircle, Users, DollarSign, Target,
  ArrowUpRight, ArrowDownRight, Minus, RefreshCw
} from 'lucide-react';
import './Relatorios.css';
import { getClients } from '../services/api';
import { getAsaasPayments } from '../services/asaasApi';

// ── API ───────────────────────────────────────────────────────────────────────
const API_BASE = '/api';

const getAuthHeaders = () => ({
  'Authorization': `Bearer ${localStorage.getItem('token')}`,
  'Content-Type': 'application/json',
});

async function apiFetch(endpoint, options = {}) {
  const res = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers: { ...getAuthHeaders(), ...options.headers },
  });
  if (!res.ok) {
    const error = await res.json().catch(() => ({ error: 'Erro na requisição' }));
    throw new Error(error.error || 'Erro na requisição');
  }
  return res.json();
}

// ── Helpers ───────────────────────────────────────────────────────────────────
const typeIcon = { CPFL: '⚡', Asaas: '💳', Pix: '📱' };

const modalityConfig = {
  CPFL:  { label: 'CPFL',  icon: '⚡', colorClass: 'amber',   accentColor: '#d97706' },
  Asaas: { label: 'Asaas', icon: '💳', colorClass: 'blue',    accentColor: '#2563eb' },
  Pix:   { label: 'Pix',   icon: '📱', colorClass: 'emerald', accentColor: '#059669' },
};

const esteiraStatusCfg = {
  rascunho:        { label: 'Rascunho',        color: 'gray'   },
  novo:            { label: 'Novo Lead',       color: 'blue'   },
  em_contato:      { label: 'Em Contato',      color: 'amber'  },
  enviar_contrato: { label: 'Contrato Enviado',color: 'indigo' },
  negociando:      { label: 'Negociando',      color: 'purple' },
  fechado:         { label: 'Fechado ✓',       color: 'green'  },
  perdido:         { label: 'Perdido',         color: 'red'    },
};

const contratoStatusCfg = {
  assinado:   { label: 'Assinado ✓',  color: 'green'  },
  aguardando: { label: 'Aguardando',  color: 'amber'  },
  expirado:   { label: 'Expirado',    color: 'gray'   },
  recusado:   { label: 'Recusado',    color: 'red'    },
};

const agendStatusCfg = {
  confirmado: { label: 'Confirmado', color: 'green'  },
  aguardando: { label: 'Pendente',   color: 'amber'  },
  pendente:   { label: 'Pendente',   color: 'amber'  },
  cancelado:  { label: 'Cancelado',  color: 'red'    },
};

function StatusBadge({ cfg, value }) {
  const c = cfg[value];
  if (!c) return <span className="rel-badge rel-badge-gray">{value}</span>;
  return <span className={`rel-badge rel-badge-${c.color}`}>{c.label}</span>;
}

function exportCSV(rows, filename) {
  const csv  = rows.map((r) => r.join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

// ── KPI Card ──────────────────────────────────────────────────────────────────
function KpiCard({ label, value, sub, color, Icon, trend }) {
  return (
    <div className={`rel-kpi-card rel-kpi-${color}`}>
      <div className="rel-kpi-top">
        <div className="rel-kpi-icon"><Icon size={18} /></div>
        {trend !== undefined && (
          <span className={`rel-kpi-trend ${trend > 0 ? 'up' : trend < 0 ? 'down' : 'flat'}`}>
            {trend > 0 ? <ArrowUpRight size={13}/> : trend < 0 ? <ArrowDownRight size={13}/> : <Minus size={13}/>}
            {Math.abs(trend)}%
          </span>
        )}
      </div>
      <div className="rel-kpi-value">{value}</div>
      <div className="rel-kpi-label">{label}</div>
      {sub && <div className="rel-kpi-sub">{sub}</div>}
    </div>
  );
}

// ── Função auxiliar para extrair dados de pagamento dos clientes ─────────────
function buildPaymentFromClient(cliente) {
  const source = cliente._source;
  const type = source === 'cpfl' ? 'CPFL' : source === 'asaas' ? 'Asaas' : 'Pix';
  const clientName = cliente.full_name || cliente.name || '—';
  
  let amount = 0;
  let dateStr = '';
  let status = 'pendente';

  if (source === 'cpfl') {
    const charge = cliente._cpflCharge;
    if (charge) {
      amount = Number(charge.valorArrecadado || charge.valor) || 0;
      const dataRef = charge.dataPagamento || charge.dataVencimento || charge.dataCadastro;
      dateStr = dataRef ? String(dataRef).slice(0, 7) : '';
      if (charge.status === 5) status = 'pago';
      else if (charge.status === 1) status = 'cancelado';
      else if (charge.status === 3 || charge.status === 6) status = 'atrasado';
      else status = 'pendente';
    }
  } else if (source === 'pix') {
    amount = Number(cliente.last_invoice_value) || 0;
    dateStr = cliente.last_invoice_date ? cliente.last_invoice_date.slice(0,7) : '';
    const situacao = (cliente.situacao || '').toUpperCase();
    if (situacao === 'ADIMPLENTE') status = 'pago';
    else if (situacao === 'INADIMPLENTE') status = 'atrasado';
    else if (situacao === 'PENDENTE') status = 'pendente';
    else status = 'pendente';
  } else if (source === 'asaas') {
    amount = Number(cliente.last_invoice_value) || 0;
    const lastStatus = cliente.last_invoice_status;
    dateStr = cliente.last_invoice_date ? cliente.last_invoice_date.slice(0,7) : '';
    if (lastStatus === 'RECEIVED') status = 'pago';
    else if (lastStatus === 'OVERDUE') status = 'atrasado';
    else if (lastStatus === 'PENDING') status = 'pendente';
    else status = 'pendente';
  }

  return {
    id: cliente.id,
    client: clientName,
    type,
    date: dateStr,
    amount,
    status,
  };
}

// ── Detalhe de modalidade (financeiro) ───────────────────────────────────────
function RelatorioDetalhe({ modality, clientes, loading, error, onBack }) {
  const [dateFrom, setDateFrom] = useState('');
  const [statusFiltro, setStatusFiltro] = useState('ativos'); // 'todos' | 'ativos' | 'cancelados'
  const [paginaAtual, setPaginaAtual] = useState(1);
  const POR_PAGINA = 50;
  const config = modalityConfig[modality];

  const payments = clientes
    .filter(c => {
      if (modality === 'CPFL') return c._source === 'cpfl';
      if (modality === 'Asaas') return c._source === 'asaas';
      return c._source === 'pix';
    })
    .map(buildPaymentFromClient);

  const cancelado = payments.filter((p) => p.status === 'cancelado').reduce((s, p) => s + p.amount, 0);
  const total    = payments.filter((p) => p.status !== 'cancelado').reduce((s, p) => s + p.amount, 0);
  const pago     = payments.filter((p) => p.status === 'pago').reduce((s, p) => s + p.amount, 0);
  const pendente = payments.filter((p) => p.status === 'pendente').reduce((s, p) => s + p.amount, 0);
  const atrasado = payments.filter((p) => p.status === 'atrasado').reduce((s, p) => s + p.amount, 0);
  const taxa     = total > 0 ? Math.round((pago / total) * 100) : 0;
  const filtered = payments
    .filter((p) => !dateFrom || p.date >= dateFrom.slice(0, 7))
    .filter((p) => {
      if (statusFiltro === 'todos') return true;
      if (statusFiltro === 'cancelados') return p.status === 'cancelado';
      return p.status !== 'cancelado'; // 'ativos'
    })
    .sort((a, b) => (b.date || '').localeCompare(a.date || ''));
  const totalPaginas = Math.max(1, Math.ceil(filtered.length / POR_PAGINA));
  const paginaSegura = Math.min(paginaAtual, totalPaginas);
  const exibidos = filtered.slice((paginaSegura - 1) * POR_PAGINA, paginaSegura * POR_PAGINA);

  if (loading) {
    return <div style={{ textAlign: 'center', padding: 40 }}>Carregando dados...</div>;
  }
  if (error) {
    return <div style={{ textAlign: 'center', padding: 40, color: '#e11d48' }}>Erro: {error}</div>;
  }

  return (
    <div className="relatorios-page">
      <div className="rel-detalhe-header">
        <button className="btn-back" onClick={onBack}>← Voltar</button>
        <div className={`rel-detalhe-title-area ${config.colorClass}`}>
          <span className="rel-detalhe-icon">{config.icon}</span>
          <div>
            <h1>Relatório — {config.label}</h1>
            <p className="page-subtitle">Detalhamento completo dos pagamentos via {config.label}</p>
          </div>
        </div>
        <button className="btn-export" onClick={() => exportCSV(
          [['Cliente','Tipo','Competência','Valor','Status'], ...filtered.map((p) => [p.client,p.type,p.date,p.amount,p.status])],
          `relatorio-${modality.toLowerCase()}.csv`
        )}>
          <Download size={14} /> Exportar CSV
        </button>
      </div>

      <div className="rel-filters">
        <input type="month" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setPaginaAtual(1); }} className="filter-input" />
        <div style={{ display: 'flex', gap: 6 }}>
          {[
            { id: 'ativos', label: 'Ativos' },
            { id: 'cancelados', label: 'Cancelados' },
            { id: 'todos', label: 'Todos' },
          ].map((opt) => (
            <button
              key={opt.id}
              type="button"
              onClick={() => { setStatusFiltro(opt.id); setPaginaAtual(1); }}
              style={{
                padding: '7px 14px',
                borderRadius: 8,
                border: statusFiltro === opt.id ? '1px solid #4f46e5' : '1px solid #e2e8f0',
                background: statusFiltro === opt.id ? '#eef2ff' : '#fff',
                color: statusFiltro === opt.id ? '#4f46e5' : '#64748b',
                fontWeight: 600,
                fontSize: 13,
                cursor: 'pointer',
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <span className="filter-hint">Filtrando por: <strong>{config.label}</strong></span>
      </div>

      <div className="detalhe-summary-grid">
        <div className={`detalhe-summary-card ${config.colorClass}`}>
          <div className="summary-label">Total Arrecadado</div>
          <div className="summary-value">R$ {total.toLocaleString('pt-BR')}</div>
        </div>
        <div className="detalhe-summary-card green">
          <div className="summary-label">Pago</div>
          <div className="summary-value">R$ {pago.toLocaleString('pt-BR')}</div>
        </div>
        <div className="detalhe-summary-card amber">
          <div className="summary-label">Pendente</div>
          <div className="summary-value">R$ {pendente.toLocaleString('pt-BR')}</div>
        </div>
        <div className="detalhe-summary-card rose">
          <div className="summary-label">Atrasado</div>
          <div className="summary-value">R$ {atrasado.toLocaleString('pt-BR')}</div>
        </div>
        <div className="detalhe-summary-card" style={{ background: '#f1f5f9' }}>
          <div className="summary-label">Cancelado</div>
          <div className="summary-value" style={{ color: '#64748b' }}>R$ {cancelado.toLocaleString('pt-BR')}</div>
        </div>
        <div className="detalhe-summary-card blue">
          <div className="summary-label">Taxa de Sucesso</div>
          <div className="summary-value">{taxa}%</div>
        </div>
      </div>

      <div className="detalhe-progress-card">
        <div className="detalhe-progress-labels">
          <span>Pagamentos realizados</span>
          <span style={{ color: config.accentColor, fontWeight: 700 }}>{taxa}%</span>
        </div>
        <div className="progress-bar-lg">
          <div className={`progress-fill-lg ${config.colorClass}`} style={{ width: `${taxa}%` }} />
        </div>
        <div className="detalhe-progress-sub">
          {payments.filter((p) => p.status === 'pago').length} de {payments.length} pagamentos realizados
        </div>
      </div>

      <div className="table-wrapper">
        <div className="table-head-title">
          Detalhamento — <span style={{ color: config.accentColor }}>{config.icon} {config.label}</span>
        </div>
        <table className="rel-table">
          <thead>
            <tr><th>Cliente</th><th>Tipo</th><th>Competência</th><th>Valor</th><th>Status</th></tr>
          </thead>
          <tbody>
            {exibidos.map((p, i) => (
              <tr key={p.id} className={i % 2 === 0 ? 'row-even' : 'row-odd'}>
                <td className="td-client">{p.client}</td>
                <td>{typeIcon[p.type]} {p.type}</td>
                <td className="td-date">{p.date || '—'}</td>
                <td className="td-amount">R$ {p.amount.toFixed(2)}</td>
                <td><span className={`badge status-${p.status}`}>{p.status}</span></td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={5} className="empty-row">Nenhum pagamento encontrado</td></tr>}
          </tbody>
        </table>
        {filtered.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 4px', fontSize: 13, color: '#64748b' }}>
            <span>Mostrando {(paginaSegura - 1) * POR_PAGINA + 1}–{Math.min(paginaSegura * POR_PAGINA, filtered.length)} de {filtered.length}</span>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <button
                type="button"
                onClick={() => setPaginaAtual((p) => Math.max(1, p - 1))}
                disabled={paginaSegura === 1}
                style={{ padding: '6px 12px', borderRadius: 6, border: '1px solid #e2e8f0', background: '#fff', cursor: paginaSegura === 1 ? 'default' : 'pointer', opacity: paginaSegura === 1 ? 0.5 : 1 }}
              >
                ← Anterior
              </button>
              <span>Página {paginaSegura} de {totalPaginas}</span>
              <button
                type="button"
                onClick={() => setPaginaAtual((p) => Math.min(totalPaginas, p + 1))}
                disabled={paginaSegura === totalPaginas}
                style={{ padding: '6px 12px', borderRadius: 6, border: '1px solid #e2e8f0', background: '#fff', cursor: paginaSegura === totalPaginas ? 'default' : 'pointer', opacity: paginaSegura === totalPaginas ? 0.5 : 1 }}
              >
                Próxima →
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// ABA 1 — ANÁLISE FINANCEIRA (com dados reais)
// ══════════════════════════════════════════════════════════════════════════════
function TabFinanceiro({ clientes, loading, error, onDrillDown }) {
  const [filterType, setFilterType] = useState('Todas as modalidades');
  const [dateFrom,   setDateFrom]   = useState('');

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-secondary, #6b7280)' }}>
        <RefreshCw size={22} className="spin" style={{ color: '#4f46e5' }} />
        <p>Carregando dados financeiros...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ textAlign: 'center', padding: 40, color: '#e11d48' }}>
        <AlertCircle size={32} style={{ marginBottom: 8 }} />
        <p>Erro ao carregar dados: {error}</p>
      </div>
    );
  }

  const payments = clientes.map(buildPaymentFromClient);

  const filtered = payments.filter((p) => {
    const matchType = filterType === 'Todas as modalidades' || p.type === filterType;
    const matchDate = !dateFrom || p.date >= dateFrom.slice(0, 7);
    return matchType && matchDate;
  });

  const total    = filtered.reduce((s, p) => s + p.amount, 0);
  const pago     = filtered.filter((p) => p.status === 'pago').reduce((s, p) => s + p.amount, 0);
  const pendente = filtered.filter((p) => p.status === 'pendente').reduce((s, p) => s + p.amount, 0);
  const taxa     = total > 0 ? Math.round((pago / total) * 100) : 0;

  const cpflTotal  = payments.filter((p) => p.type === 'CPFL').reduce((s, p) => s + p.amount, 0);
  const asaasTotal = payments.filter((p) => p.type === 'Asaas').reduce((s, p) => s + p.amount, 0);
  const pixTotal   = payments.filter((p) => p.type === 'Pix').reduce((s, p) => s + p.amount, 0);
  const grandTotal = cpflTotal + asaasTotal + pixTotal;

  return (
    <>
      <div className="rel-section-header">
        <div>
          <h2 className="rel-section-title">Análise Financeira</h2>
          <p className="rel-section-sub">Visão geral de pagamentos e arrecadação</p>
        </div>
        <button className="btn-export" onClick={() => exportCSV(
          [['Cliente','Tipo','Competência','Valor','Status'], ...filtered.map((p) => [p.client,p.type,p.date,p.amount,p.status])],
          'relatorio-financeiro.csv'
        )}>
          <Download size={14} /> Exportar CSV
        </button>
      </div>

      <div className="rel-filters">
        <input type="month" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="filter-input" />
        <select value={filterType} onChange={(e) => setFilterType(e.target.value)} className="filter-select">
          <option>Todas as modalidades</option>
          <option>CPFL</option>
          <option>Asaas</option>
          <option>Pix</option>
        </select>
      </div>

      {/* Modalidades clicáveis */}
      <div className="modality-card">
        <h3>Detalhamento por Modalidade</h3>
        <p className="modality-hint">Clique em uma modalidade para ver o relatório detalhado</p>
        <div className="modality-grid">
          {[
            { id: 'CPFL',  icon: '⚡', label: 'CPFL',  value: cpflTotal,  pct: grandTotal ? Math.round(cpflTotal  / grandTotal * 100) : 0, colorClass: 'amber'   },
            { id: 'Asaas', icon: '💳', label: 'Asaas', value: asaasTotal, pct: grandTotal ? Math.round(asaasTotal / grandTotal * 100) : 0, colorClass: 'blue'    },
            { id: 'Pix',   icon: '📱', label: 'Pix',   value: pixTotal,   pct: grandTotal ? Math.round(pixTotal   / grandTotal * 100) : 0, colorClass: 'emerald' },
          ].map((m) => (
            <div key={m.id} className={`modality-item ${m.colorClass} clickable`} onClick={() => onDrillDown(m.id)}>
              <div className="modality-top">
                <span className="modality-icon">{m.icon}</span>
                <span className="modality-name">{m.label}</span>
                <span className="modality-arrow">→</span>
              </div>
              <div className="modality-value">R$ {m.value.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</div>
              <div className="progress-bar">
                <div className={`progress-fill ${m.colorClass}`} style={{ width: `${m.pct}%` }} />
              </div>
              <span className="modality-pct">{m.pct}% do total</span>
              <span className="modality-cta">Ver detalhes →</span>
            </div>
          ))}
        </div>
      </div>

      {/* Tabela de pagamentos filtrada */}
      <div className="table-wrapper">
        <div className="table-head-title">
          Pagamentos {filterType !== 'Todas as modalidades' && `- ${filterType}`}
        </div>
        <table className="rel-table">
          <thead>
            <tr><th>Cliente</th><th>Tipo</th><th>Competência</th><th>Valor</th><th>Status</th></tr>
          </thead>
          <tbody>
            {filtered.map((p, i) => (
              <tr key={p.id} className={i % 2 === 0 ? 'row-even' : 'row-odd'}>
                <td className="td-client">{p.client}</td>
                <td>{typeIcon[p.type]} {p.type}</td>
                <td className="td-date">{p.date || '—'}</td>
                <td className="td-amount">R$ {p.amount.toFixed(2)}</td>
                <td><span className={`badge status-${p.status}`}>{p.status}</span></td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={5} className="empty-row">Nenhum pagamento encontrado</td></tr>}
          </tbody>
        </table>
      </div>
    </>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// ABA 2 — ESTEIRA DE VENDAS (mantida igual ao original)
// ══════════════════════════════════════════════════════════════════════════════
function TabEsteira() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterVendedor, setFilterVendedor] = useState('Todos');
  const [filterStatus, setFilterStatus] = useState('Todos');
  const [filterMod, setFilterMod] = useState('Todas');

  const getValorNumerico = (lead) => {
    if (lead.valor === undefined || lead.valor === null) return 0;
    if (typeof lead.valor === 'number') return lead.valor;
    const parsed = parseFloat(lead.valor);
    return isNaN(parsed) ? 0 : parsed;
  };

  const loadLeads = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiFetch('/leads');
      const leadsWithValue = data.map(lead => ({
        ...lead,
        valor: getValorNumerico(lead),
      }));
      setLeads(leadsWithValue);
    } catch (err) {
      console.error('Erro ao carregar leads da esteira:', err);
      setError(err.message || 'Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLeads();
  }, []);

  const vendedores = [...new Set(leads.map(l => l.vendedor).filter(Boolean))];

  const filtered = leads.filter(lead => {
    const matchV = filterVendedor === 'Todos' || lead.vendedor === filterVendedor;
    const matchS = filterStatus === 'Todos'   || lead.status === filterStatus;
    const matchM = filterMod === 'Todas'      || lead.modalidade === filterMod;
    return matchV && matchS && matchM;
  });

  const totalLeads    = leads.length;
  const fechados      = leads.filter(l => l.status === 'fechado').length;
  const emAndamento   = leads.filter(l => !['fechado','perdido'].includes(l.status)).length;
  const taxaConversao = totalLeads > 0 ? Math.round((fechados / totalLeads) * 100) : 0;
  const totalReceita  = leads
    .filter(l => l.status === 'fechado')
    .reduce((s, l) => s + getValorNumerico(l), 0);

  const rankVendedor = vendedores.map(v => {
    const leadsVendedor = leads.filter(l => l.vendedor === v);
    const fechou = leadsVendedor.filter(l => l.status === 'fechado').length;
    const receita = leadsVendedor
      .filter(l => l.status === 'fechado')
      .reduce((s, l) => s + getValorNumerico(l), 0);
    return {
      vendedor: v,
      total: leadsVendedor.length,
      fechou,
      taxa: leadsVendedor.length > 0 ? Math.round((fechou / leadsVendedor.length) * 100) : 0,
      receita,
    };
  });

  const funil = [
    { label: 'Rascunho',        count: leads.filter(l => l.status === 'rascunho').length,        color: '#9ca3af', emoji: '📝' },
    { label: 'Novo Lead',       count: leads.filter(l => l.status === 'novo').length,             color: '#3b82f6', emoji: '🆕' },
    { label: 'Em Contato',      count: leads.filter(l => l.status === 'em_contato').length,       color: '#f59e0b', emoji: '📞' },
    { label: 'Contrato Enviado',count: leads.filter(l => l.status === 'enviar_contrato').length, color: '#6366f1', emoji: '📧' },
    { label: 'Negociando',      count: leads.filter(l => l.status === 'negociando').length,       color: '#8b5cf6', emoji: '🤝' },
    { label: 'Fechado',         count: leads.filter(l => l.status === 'fechado').length,          color: '#10b981', emoji: '🏆' },
    { label: 'Perdido',         count: leads.filter(l => l.status === 'perdido').length,          color: '#ef4444', emoji: '❌' },
  ].filter(f => f.count > 0);

  if (loading) {
    return <div style={{ textAlign: 'center', padding: 40 }}>Carregando leads da esteira...</div>;
  }

  if (error) {
    return (
      <div style={{ textAlign: 'center', padding: 40, color: '#e11d48' }}>
        <AlertCircle size={32} style={{ marginBottom: 8 }} />
        <p>Erro ao carregar esteira de vendas: {error}</p>
        <button className="btn-export" onClick={loadLeads} style={{ marginTop: 16 }}>Tentar novamente</button>
      </div>
    );
  }

  return (
    <>
      <div className="rel-section-header">
        <div>
          <h2 className="rel-section-title">Esteira de Vendas</h2>
          <p className="rel-section-sub">Desempenho e funil de leads da equipe comercial</p>
        </div>
        <button className="btn-export" onClick={() => exportCSV(
          [['Nome','Vendedor','Modalidade','Status','Data','Valor'],
           ...filtered.map(l => [l.nome, l.vendedor, l.modalidade, l.status, l.dataVenda, getValorNumerico(l)])],
          'relatorio-esteira.csv'
        )}>
          <Download size={14} /> Exportar CSV
        </button>
      </div>

      <div className="rel-kpi-grid">
        <KpiCard label="Total de Leads"    value={totalLeads}        color="blue"   Icon={Users}       />
        <KpiCard label="Fechados"          value={fechados}          color="green"  Icon={CheckCircle} trend={taxaConversao} />
        <KpiCard label="Em Andamento"      value={emAndamento}       color="amber"  Icon={Clock}       />
        <KpiCard label="Taxa de Conversão" value={`${taxaConversao}%`} color="purple" Icon={Target}      />
        <KpiCard label="Receita Fechada"   value={`R$ ${totalReceita.toLocaleString('pt-BR')}`} color="green" Icon={DollarSign} />
      </div>

      {funil.length > 0 && (
        <div className="rel-funnel-card">
          <h3 className="rel-card-title">Funil de Vendas</h3>
          {funil.map((f) => (
            <div key={f.label} className="rel-funnel-row">
              <div className="rel-funnel-label"><span>{f.emoji}</span><span>{f.label}</span></div>
              <div className="rel-funnel-bar-wrap">
                <div className="rel-funnel-bar-bg">
                  <div className="rel-funnel-bar-fill" style={{ width: totalLeads > 0 ? `${(f.count / totalLeads) * 100}%` : '0%', background: f.color }} />
                </div>
              </div>
              <span className="rel-funnel-count">{f.count}</span>
            </div>
          ))}
        </div>
      )}

      {rankVendedor.length > 0 && (
        <div className="rel-card" style={{ marginBottom: 24 }}>
          <h3 className="rel-card-title">Ranking de Vendedores</h3>
          <div className="rel-ranking-table-wrap">
            <table className="rel-table">
              <thead><tr><th>#</th><th>Vendedor</th><th>Leads</th><th>Fechados</th><th>Taxa</th><th>Receita</th></tr></thead>
              <tbody>
                {rankVendedor.sort((a,b) => b.fechou - a.fechou).map((r, i) => (
                  <tr key={r.vendedor} className={i % 2 === 0 ? 'row-even' : 'row-odd'}>
                    <td><span className="rel-rank-num">{i + 1}</span></td>
                    <td className="td-client">{r.vendedor}</td>
                    <td>{r.total}</td>
                    <td><strong style={{ color: '#059669' }}>{r.fechou}</strong></td>
                    <td><div className="rel-taxa-wrap"><div className="rel-taxa-bar" style={{ width: `${r.taxa}%` }} /><span>{r.taxa}%</span></div></td>
                    <td className="td-amount">R$ {r.receita.toLocaleString('pt-BR')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="rel-card">
        <div className="rel-card-header">
          <h3 className="rel-card-title">Leads Detalhados</h3>
          <div className="rel-card-filters">
            <select className="filter-select" value={filterVendedor} onChange={(e) => setFilterVendedor(e.target.value)}>
              <option value="Todos">Todos os vendedores</option>
              {vendedores.map(v => <option key={v} value={v}>{v}</option>)}
            </select>
            <select className="filter-select" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
              <option value="Todos">Todos os status</option>
              {Object.entries(esteiraStatusCfg).map(([key, cfg]) => <option key={key} value={key}>{cfg.label}</option>)}
            </select>
            <select className="filter-select" value={filterMod} onChange={(e) => setFilterMod(e.target.value)}>
              <option value="Todas">Todas modalidades</option>
              <option value="CPFL">CPFL</option>
              <option value="Asaas">Asaas</option>
              <option value="Pix">Pix</option>
            </select>
          </div>
        </div>
        <table className="rel-table">
          <thead><tr><th>Cliente</th><th>Vendedor</th><th>Modalidade</th><th>Data</th><th>Valor</th><th>Status</th></tr></thead>
          <tbody>
            {filtered.map((lead, i) => (
              <tr key={lead.id} className={i % 2 === 0 ? 'row-even' : 'row-odd'}>
                <td className="td-client">{lead.nome}</td>
                <td>{lead.vendedor || '-'}</td>
                <td>{typeIcon[lead.modalidade]} {lead.modalidade}</td>
                <td className="td-date">{lead.dataVenda}</td>
                <td className="td-amount">R$ {getValorNumerico(lead).toLocaleString('pt-BR')}</td>
                <td><StatusBadge cfg={esteiraStatusCfg} value={lead.status} /></td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={6} className="empty-row">Nenhum lead encontrado</td></tr>}
          </tbody>
        </table>
      </div>
    </>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// ABA 3 — CONTRATOS ENVIADOS (mantida igual ao original)
// ══════════════════════════════════════════════════════════════════════════════
const mockContratos = [
  { id: 1, cliente: 'SILVANA FEITOZA',  vendedor: 'Carlos Oliveira', modalidade: 'CPFL',  enviadoEm: '2026-03-19 09:10', status: 'assinado',   canal: 'WhatsApp' },
  { id: 2, cliente: 'MARIA APARECIDA',  vendedor: 'Ana Lima',        modalidade: 'Asaas', enviadoEm: '2026-03-19 10:22', status: 'aguardando', canal: 'E-mail'   },
  { id: 3, cliente: 'ROBERTO FERREIRA', vendedor: 'Carlos Oliveira', modalidade: 'Pix',   enviadoEm: '2026-03-18 14:05', status: 'expirado',   canal: 'WhatsApp' },
  { id: 4, cliente: 'FERNANDA LIMA',    vendedor: 'Ana Lima',        modalidade: 'CPFL',  enviadoEm: '2026-03-18 16:30', status: 'assinado',   canal: 'SMS'      },
  { id: 5, cliente: 'PEDRO ALVES',      vendedor: 'Carlos Oliveira', modalidade: 'Asaas', enviadoEm: '2026-03-17 11:00', status: 'aguardando', canal: 'WhatsApp' },
  { id: 6, cliente: 'LUCIA MENDES',     vendedor: 'Ana Lima',        modalidade: 'Pix',   enviadoEm: '2026-03-17 08:45', status: 'recusado',   canal: 'E-mail'   },
  { id: 7, cliente: 'JOÃO COSTA',       vendedor: 'Carlos Oliveira', modalidade: 'CPFL',  enviadoEm: '2026-03-16 09:00', status: 'assinado',   canal: 'WhatsApp' },
  { id: 8, cliente: 'ANA RODRIGUES',    vendedor: 'Ana Lima',        modalidade: 'Asaas', enviadoEm: '2026-03-16 11:30', status: 'assinado',   canal: 'E-mail'   },
];

function TabContratos() {
  const [filterStatus, setFilterStatus] = useState('Todos');
  const [filterMod,    setFilterMod]    = useState('Todas');

  const filtered = mockContratos.filter((c) => {
    const matchS = filterStatus === 'Todos' || c.status     === filterStatus;
    const matchM = filterMod    === 'Todas' || c.modalidade === filterMod;
    return matchS && matchM;
  });

  const assinados  = mockContratos.filter((c) => c.status === 'assinado').length;
  const aguardando = mockContratos.filter((c) => c.status === 'aguardando').length;
  const expirados  = mockContratos.filter((c) => c.status === 'expirado').length;
  const recusados  = mockContratos.filter((c) => c.status === 'recusado').length;
  const taxa       = mockContratos.length > 0 ? Math.round((assinados / mockContratos.length) * 100) : 0;

  return (
    <>
      <div className="rel-section-header">
        <div>
          <h2 className="rel-section-title">Contratos Enviados</h2>
          <p className="rel-section-sub">Status e rastreamento de contratos digitais</p>
        </div>
        <button className="btn-export" onClick={() => exportCSV(
          [['Cliente','Vendedor','Modalidade','Canal','Enviado em','Status'],
           ...filtered.map((c) => [c.cliente, c.vendedor, c.modalidade, c.canal, c.enviadoEm, c.status])],
          'relatorio-contratos.csv'
        )}>
          <Download size={14} /> Exportar CSV
        </button>
      </div>

      <div className="rel-kpi-grid">
        <KpiCard label="Total Enviados" value={mockContratos.length} color="blue"   Icon={FileText}    />
        <KpiCard label="Assinados"      value={assinados}            color="green"  Icon={CheckCircle} trend={taxa} />
        <KpiCard label="Aguardando"     value={aguardando}           color="amber"  Icon={Clock}       />
        <KpiCard label="Expirados"      value={expirados}            color="gray"   Icon={XCircle}     />
        <KpiCard label="Taxa de Assin." value={`${taxa}%`}           color="purple" Icon={Target}      />
      </div>

      <div className="detalhe-progress-card">
        <div className="detalhe-progress-labels">
          <span>Taxa de assinatura</span>
          <span style={{ color: '#059669', fontWeight: 700 }}>{taxa}%</span>
        </div>
        <div className="progress-bar-lg">
          <div className="progress-fill-lg emerald" style={{ width: `${taxa}%` }} />
        </div>
        <div className="detalhe-progress-sub">
          {assinados} de {mockContratos.length} contratos assinados · {recusados} recusados · {expirados} expirados
        </div>
      </div>

      <div className="rel-card">
        <div className="rel-card-header">
          <h3 className="rel-card-title">Contratos Detalhados</h3>
          <div className="rel-card-filters">
            <select className="filter-select" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
              <option value="Todos">Todos os status</option>
              <option value="assinado">Assinado</option>
              <option value="aguardando">Aguardando</option>
              <option value="expirado">Expirado</option>
              <option value="recusado">Recusado</option>
            </select>
            <select className="filter-select" value={filterMod} onChange={(e) => setFilterMod(e.target.value)}>
              <option value="Todas">Todas modalidades</option>
              <option value="CPFL">CPFL</option>
              <option value="Asaas">Asaas</option>
              <option value="Pix">Pix</option>
            </select>
          </div>
        </div>
        <table className="rel-table">
          <thead>
            <tr><th>Cliente</th><th>Vendedor</th><th>Modalidade</th><th>Canal</th><th>Enviado em</th><th>Status</th></tr>
          </thead>
          <tbody>
            {filtered.map((c, i) => (
              <tr key={c.id} className={i % 2 === 0 ? 'row-even' : 'row-odd'}>
                <td className="td-client">{c.cliente}</td>
                <td>{c.vendedor}</td>
                <td>{typeIcon[c.modalidade]} {c.modalidade}</td>
                <td className="td-date">{c.canal}</td>
                <td className="td-date">{c.enviadoEm}</td>
                <td><StatusBadge cfg={contratoStatusCfg} value={c.status} /></td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={6} className="empty-row">Nenhum contrato encontrado</td></tr>}
          </tbody>
        </table>
      </div>
    </>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// ABA 4 — AGENDAMENTOS (mantida igual ao original)
// ══════════════════════════════════════════════════════════════════════════════
function TabAgendamentos() {
  const [agendamentos,  setAgendamentos]  = useState([]);
  const [medicos,       setMedicos]       = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [erro,          setErro]          = useState(null);
  const [filterStatus,  setFilterStatus]  = useState('Todos');
  const [filterTipo,    setFilterTipo]    = useState('Todos');

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setErro(null);
        const [agendamentosData, medicosData] = await Promise.all([
          apiFetch('/agendamentos'),
          apiFetch('/medicos'),
        ]);
        setAgendamentos(agendamentosData);
        setMedicos(medicosData);
      } catch (err) {
        console.error('Erro ao carregar dados de agendamentos:', err);
        setErro(err.message || 'Erro ao carregar dados');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-secondary, #6b7280)' }}>
        Carregando agendamentos...
      </div>
    );
  }

  if (erro) {
    return (
      <div style={{ textAlign: 'center', padding: 40, color: '#e11d48' }}>
        <AlertCircle size={32} style={{ marginBottom: 8 }} />
        <p>Erro ao carregar agendamentos: {erro}</p>
      </div>
    );
  }

  const enrichedAgendamentos = agendamentos.map((ag) => {
    const medico = medicos.find((m) => m.id === ag.medicoId);
    let tipo = 'Consulta Médica';
    if (medico && medico.especialidade && medico.especialidade.toLowerCase().includes('odont')) {
      tipo = 'Odontologia';
    }
    return {
      id:           ag.id,
      cliente:      ag.paciente,
      tipo:         tipo,
      data:         ag.data,
      hora:         ag.horario,
      profissional: medico ? medico.nome : 'Profissional não informado',
      status:       ag.status,
    };
  });

  const filtered = enrichedAgendamentos.filter((a) => {
    const matchS = filterStatus === 'Todos' || a.status === filterStatus;
    const matchT = filterTipo   === 'Todos' || a.tipo   === filterTipo;
    return matchS && matchT;
  });

  const total       = enrichedAgendamentos.length;
  const confirmados = enrichedAgendamentos.filter((a) => a.status === 'confirmado').length;
  const pendentes   = enrichedAgendamentos.filter((a) => a.status === 'aguardando' || a.status === 'pendente').length;
  const cancelados  = enrichedAgendamentos.filter((a) => a.status === 'cancelado').length;
  const taxa        = total > 0 ? Math.round((confirmados / total) * 100) : 0;

  const tipos  = [...new Set(enrichedAgendamentos.map((a) => a.tipo))];
  const byTipo = tipos.map((tipo) => ({
    tipo,
    total:      enrichedAgendamentos.filter((a) => a.tipo === tipo).length,
    confirmado: enrichedAgendamentos.filter((a) => a.tipo === tipo && a.status === 'confirmado').length,
    cancelado:  enrichedAgendamentos.filter((a) => a.tipo === tipo && a.status === 'cancelado').length,
  }));

  const profissionais = [...new Set(enrichedAgendamentos.map((a) => a.profissional))];
  const byProf = profissionais.map((p) => ({
    profissional: p,
    total:        enrichedAgendamentos.filter((a) => a.profissional === p).length,
    confirmado:   enrichedAgendamentos.filter((a) => a.profissional === p && a.status === 'confirmado').length,
  }));

  return (
    <>
      <div className="rel-section-header">
        <div>
          <h2 className="rel-section-title">Agendamentos</h2>
          <p className="rel-section-sub">Consultas e procedimentos agendados</p>
        </div>
        <button className="btn-export" onClick={() => exportCSV(
          [['Cliente','Tipo','Data','Hora','Profissional','Status'],
           ...filtered.map((a) => [a.cliente, a.tipo, a.data, a.hora, a.profissional, a.status])],
          'relatorio-agendamentos.csv'
        )}>
          <Download size={14} /> Exportar CSV
        </button>
      </div>

      <div className="rel-kpi-grid">
        <KpiCard label="Total Agendados"  value={total}           color="blue"   Icon={Calendar}    />
        <KpiCard label="Confirmados"      value={confirmados}     color="green"  Icon={CheckCircle} trend={taxa} />
        <KpiCard label="Pendentes"        value={pendentes}       color="amber"  Icon={Clock}       />
        <KpiCard label="Cancelados"       value={cancelados}      color="red"    Icon={XCircle}     />
        <KpiCard label="Taxa Confirmação" value={`${taxa}%`}      color="purple" Icon={Target}      />
      </div>

      <div className="rel-kpi-grid" style={{ gridTemplateColumns: 'repeat(2,1fr)', marginBottom: 24 }}>
        {byTipo.map((t) => (
          <div key={t.tipo} className="rel-card rel-card-inline">
            <div className="rel-card-title-sm">{t.tipo}</div>
            <div className="rel-bytype-row">
              <div className="rel-bytype-item">
                <span className="rel-bytype-val" style={{ color: '#059669' }}>{t.confirmado}</span>
                <span className="rel-bytype-label">Confirmados</span>
              </div>
              <div className="rel-bytype-item">
                <span className="rel-bytype-val" style={{ color: '#e11d48' }}>{t.cancelado}</span>
                <span className="rel-bytype-label">Cancelados</span>
              </div>
              <div className="rel-bytype-item">
                <span className="rel-bytype-val">{t.total}</span>
                <span className="rel-bytype-label">Total</span>
              </div>
            </div>
            <div className="rel-funnel-bar-bg" style={{ marginTop: 10 }}>
              <div
                className="rel-funnel-bar-fill"
                style={{ width: `${t.total > 0 ? Math.round((t.confirmado / t.total) * 100) : 0}%`, background: '#059669' }}
              />
            </div>
          </div>
        ))}
      </div>

      <div className="rel-card" style={{ marginBottom: 24 }}>
        <h3 className="rel-card-title">Por Profissional</h3>
        <table className="rel-table">
          <thead>
            <tr><th>Profissional</th><th>Total</th><th>Confirmados</th><th>Taxa</th></tr>
          </thead>
          <tbody>
            {byProf.map((p, i) => (
              <tr key={p.profissional} className={i % 2 === 0 ? 'row-even' : 'row-odd'}>
                <td className="td-client">{p.profissional}</td>
                <td>{p.total}</td>
                <td><strong style={{ color: '#059669' }}>{p.confirmado}</strong></td>
                <td>
                  <div className="rel-taxa-wrap">
                    <div
                      className="rel-taxa-bar"
                      style={{ width: `${p.total > 0 ? Math.round((p.confirmado / p.total) * 100) : 0}%`, background: '#059669' }}
                    />
                    <span>{p.total > 0 ? Math.round((p.confirmado / p.total) * 100) : 0}%</span>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="rel-card">
        <div className="rel-card-header">
          <h3 className="rel-card-title">Agenda Detalhada</h3>
          <div className="rel-card-filters">
            <select className="filter-select" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
              <option value="Todos">Todos os status</option>
              <option value="confirmado">Confirmado</option>
              <option value="aguardando">Pendente</option>
              <option value="cancelado">Cancelado</option>
            </select>
            <select className="filter-select" value={filterTipo} onChange={(e) => setFilterTipo(e.target.value)}>
              <option value="Todos">Todos os tipos</option>
              <option value="Consulta Médica">Consulta Médica</option>
              <option value="Odontologia">Odontologia</option>
            </select>
          </div>
        </div>
        <table className="rel-table">
          <thead>
            <tr><th>Cliente</th><th>Tipo</th><th>Data</th><th>Hora</th><th>Profissional</th><th>Status</th></tr>
          </thead>
          <tbody>
            {filtered.map((a, i) => (
              <tr key={a.id} className={i % 2 === 0 ? 'row-even' : 'row-odd'}>
                <td className="td-client">{a.cliente}</td>
                <td>{a.tipo}</td>
                <td className="td-date">{a.data}</td>
                <td className="td-date">{a.hora}</td>
                <td>{a.profissional}</td>
                <td><StatusBadge cfg={agendStatusCfg} value={a.status} /></td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={6} className="empty-row">Nenhum agendamento encontrado</td></tr>}
          </tbody>
        </table>
      </div>
    </>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// COMPONENTE PRINCIPAL
// ══════════════════════════════════════════════════════════════════════════════
const TABS = [
  { id: 'financeiro',   label: 'Análise Financeira',    Icon: BarChart2   },
  { id: 'esteira',      label: 'Esteira de Vendas',     Icon: TrendingUp  },
  { id: 'contratos',    label: 'Contratos Enviados',    Icon: FileText    },
  { id: 'agendamentos', label: 'Agendamentos',           Icon: Calendar    },
];

function Relatorios() {
  const [activeTab,     setActiveTab]     = useState('financeiro');
  const [drillModality, setDrillModality] = useState(null);
  const [clientes,      setClientes]      = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [error,         setError]         = useState(null);

  useEffect(() => {
    async function carregarClientes() {
      setLoading(true);
      setError(null);
      try {
        const [cpflResp, pixResp, asaasResp, cpflChargesResp, asaasPaymentsResp] = await Promise.all([
          getClients('payment_type=cpfl&limit=3000'),
          getClients('payment_type=pix&limit=3000'),
          getClients('payment_type=asaas&limit=3000'),
          apiFetch('/cpfl/cobrancas?limit=5000'),
          apiFetch('/asaas/local-payments?limit=20000'),
        ]);

        const cpflData = cpflResp?.data || (Array.isArray(cpflResp) ? cpflResp : []);
        const cpflChargesPorCpf = new Map();
        (cpflChargesResp?.data || []).forEach((charge) => {
          const cpfLimpo = (charge.clienteCpf || '').replace(/\D/g, '');
          if (!cpfLimpo) return;
          const atual = cpflChargesPorCpf.get(cpfLimpo);
          const dataCharge = charge.dataVencimento || charge.dataCadastro || '';
          const dataAtual = atual ? (atual.dataVencimento || atual.dataCadastro || '') : '';
          if (!atual || dataCharge > dataAtual) {
            cpflChargesPorCpf.set(cpfLimpo, charge);
          }
        });
        const pixData = pixResp?.data || (Array.isArray(pixResp) ? pixResp : []);
        let asaasData = asaasResp?.data || (Array.isArray(asaasResp) ? asaasResp : []);

        // ⚠️ Antes disparava 1 requisição POR cliente Asaas em paralelo — com
        // milhares de clientes isso estourava o limite de conexões do navegador
        // (ERR_INSUFFICIENT_RESOURCES). Agora busca todos os pagamentos numa
        // única chamada e agrupa por cliente no próprio navegador.
        const asaasPagamentoPorCustomer = new Map();
        (asaasPaymentsResp?.data || []).forEach((payment) => {
          if (!payment.customer) return;
          const atual = asaasPagamentoPorCustomer.get(payment.customer);
          if (!atual || new Date(payment.dueDate || 0) > new Date(atual.dueDate || 0)) {
            asaasPagamentoPorCustomer.set(payment.customer, payment);
          }
        });
        const asaasEnriquecidos = asaasData.map((cliente) => {
          const latest = cliente.asaas_customer_id
            ? asaasPagamentoPorCustomer.get(cliente.asaas_customer_id)
            : null;
          return {
            ...cliente,
            _source: 'asaas',
            last_invoice_date: latest?.dueDate || null,
            last_invoice_status: latest?.status || null,
            last_invoice_value: latest?.value || 0,
          };
        });

        const cpflLista = cpflData.map(c => ({
          ...c,
          _source: 'cpfl',
          _cpflCharge: cpflChargesPorCpf.get((c.cpf || '').replace(/\D/g, '')) || null,
        }));
        const pixLista = pixData.map(c => ({ ...c, _source: 'pix' }));

        const map = new Map();
        [...asaasEnriquecidos, ...cpflLista, ...pixLista].forEach(c => {
          const key = c.cpf || c.id;
          if (!map.has(key)) map.set(key, c);
        });

        setClientes(Array.from(map.values()));
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }
    carregarClientes();
  }, []);

  if (activeTab === 'financeiro' && drillModality) {
    return (
      <RelatorioDetalhe
        modality={drillModality}
        clientes={clientes}
        loading={loading}
        error={error}
        onBack={() => setDrillModality(null)}
      />
    );
  }

  return (
    <div className="relatorios-page">
      <div className="relatorios-header">
        <div>
          <h1>Relatórios</h1>
          <p className="page-subtitle">Central de análise e desempenho</p>
        </div>
      </div>

      <div className="rel-tabs-bar">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            className={`rel-tab-btn ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => { setActiveTab(tab.id); setDrillModality(null); }}
          >
            <tab.Icon size={15} />
            {tab.label}
          </button>
        ))}
      </div>

      <div className="rel-tab-content">
        {activeTab === 'financeiro'   && <TabFinanceiro clientes={clientes} loading={loading} error={error} onDrillDown={setDrillModality} />}
        {activeTab === 'esteira'      && <TabEsteira      />}
        {activeTab === 'contratos'    && <TabContratos    />}
        {activeTab === 'agendamentos' && <TabAgendamentos />}
      </div>
    </div>
  );
}

export default Relatorios;